import React, { ReactNode, useEffect, useMemo, useState } from 'react'
import * as S from './style'
import SearchInput from './search'
import Head from './head'
import { ActionProps, TableHeaderProps } from './types'
import useDebounce from '@hooks/useDebounce'
import Pagination from '@components/pagination'
import Body from './body'

export interface TableProps {
  title?: string
  columns?: TableHeaderProps[]
  data?: any[]
  searchKeys?: string[]
  totalPages?: number
  totalItems?: number
  itemsPerPage?: number
  hasExportButton?: boolean
  hasText?: boolean
  text?: string
  hasSearchbar?: boolean
  onExportClick?: () => void
  onSearchChange?: (val: string) => void
  pageControlled?: number
  onPageChange?: (page: number) => void
  styleText?: any
  icon?: string
  actions?: ActionProps[]
  expandedRowRender?: any
  borderRadius?: number
  headerBgColor?: string
  onExpand?: (rowData: any) => void
  setId?: (val: string, limit?: number) => void
  setLimit?: (val: number) => void
  onSave?: (data: any) => void
  hasSaveButton?: boolean
  onRowClick?: (data: any) => void
  loading?: boolean
  clientSearch?: boolean
  rightSearchElement?: ReactNode
}

const Table: React.FC<TableProps> = ({
  title,
  columns = [],
  data = [],
  totalPages,
  totalItems,
  itemsPerPage,
  hasExportButton = false,
  text,
  searchKeys = ['email', 'name', 'phone'],
  hasSearchbar = true,
  onExportClick,
  onSearchChange,
  pageControlled,
  onPageChange,
  styleText,
  icon,
  actions,
  expandedRowRender,
  headerBgColor,
  borderRadius,
  setId,
  setLimit,
  onSave,
  hasSaveButton,
  onRowClick,
  loading,
  clientSearch = false,
  rightSearchElement,
}) => {
  const [pageInternal, setPageInternal] = useState<number>(1)
  const [searchInput, setSearchInput] = useState('')

  const page = pageControlled ?? pageInternal
  const setPage = onPageChange ?? setPageInternal

  const debouncedValue = useDebounce(searchInput, 200)

  useEffect(() => {
    onSearchChange?.(debouncedValue)
  }, [debouncedValue, onSearchChange])

  const dummyColumns = [
    {
      title: 'SN',
      key: 'sn',
      render: (_item: any, index: number) => index + 1,
    },
    {
      title: 'Name',
      key: 'name',
    },
    {
      title: 'Email',
      key: 'email',
    },
    {
      title: 'Age',
      key: 'age',
    },
  ]

  const dummyData = []

  const actualData = data?.length ? data : dummyData
  const actualColumns = columns?.length ? columns : dummyColumns

  const filteredData = actualData.filter((d) =>
    searchKeys.some((key) =>
      d[key]
        ?.toString()
        .toLowerCase()
        .trim()
        .includes(debouncedValue.toLowerCase().trim()),
    ),
  )

  return (
    <>
      <S.StyleTableWrapper borderRadius={borderRadius}>
        <S.StyleTableTitle>{title}</S.StyleTableTitle>
        {!!hasSearchbar && (
          <SearchInput
            text={text}
            hasButton={hasExportButton}
            onButtonClick={onExportClick}
            searchInput={searchInput}
            setSearchInput={setSearchInput}
            placeholder={searchKeys}
            styleText={styleText}
            icon={icon}
            rightSearchElement={rightSearchElement}
          />
        )}

        <S.StyleTableResponsiveWrapper>
          <S.StyleTable>
            <Head
              columns={actualColumns}
              actions={actions}
              hasExpandable={!!expandedRowRender}
              headerBgColor={headerBgColor}
            />
            <Body
              // loading={loading}
              columns={actualColumns}
              data={hasSearchbar && clientSearch ? filteredData : actualData}
              actions={actions}
              expandedRowRender={expandedRowRender}
              setId={setId}
              setLimit={setLimit}
              onSave={onSave}
              hasSaveButton={hasSaveButton}
              onRowClick={onRowClick}
            />
          </S.StyleTable>
        </S.StyleTableResponsiveWrapper>
      </S.StyleTableWrapper>
      <Pagination
        isShown={totalItems > itemsPerPage}
        currentPage={page}
        totalPages={totalPages}
        itemsPerpage={itemsPerPage}
        setPage={setPage}
        totalItems={totalItems}
      />
    </>
  )
}

export default Table
